import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppBooking from '../components/WhatsAppBooking';
import SEOJsonLd from '../../../components/base/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thesunshinehealing.com';

const studioImage = 'https://public.readdy.ai/ai/img_res/561a5639c2d8c3dc8e54df329358952e.jpg spiritual healing studio banner design with glowing lotus flowers chakra rainbow energy light sacred geometry golden patterns serene meditation space white and gold cream color palette peaceful divine atmosphere ethereal glow natural elements Singapore wellness brand beautiful harmonious spiritual landscape with soft light rays and healing crystals&width=1200&height=630&seq=studio-og-preview-001&orientation=landscape';

const umanaJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'UMANA Energy Healing Singapore — Activate Chakras 8–12 Soul Empowerment',
    description:
      'UMANA integrative energy healing in Singapore. Activate Chakras 8–12 above the physical body for massive consciousness shifts, soul empowerment and evolutionary breakthroughs. In-person and remote. Sessions from SGD $80.',
    url: `${siteUrl}/services/umana`,
    image: studioImage,
    areaServed: 'Singapore',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Sunshine Healing Studio',
      url: siteUrl,
      telephone: '+6593651286',
      address: { '@type': 'PostalAddress', addressLocality: 'Singapore', addressCountry: 'SG' },
    },
    offers: [
      { '@type': 'Offer', price: '80', priceCurrency: 'SGD', description: 'Light UMANA Session (30 mins) — Chakras 8–10' },
      { '@type': 'Offer', price: '150', priceCurrency: 'SGD', description: 'Deep UMANA Session (60 mins) — Chakras 8–12' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页 Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'UMANA 能量疗愈', item: `${siteUrl}/services/umana` },
    ],
  },
];

export default function UmanaPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-white">
      <title>UMANA 能量疗愈 新加坡 | UMANA Healing Singapore — Activate Chakras 8-12</title>
      <meta name="description" content="新加坡UMANA能量疗愈，激活物质身体之外的第8-12脉轮，带来巨大意识转变与灵魂赋能。可远程或面对面，30-60分钟，SGD $80起。Singapore UMANA healing — activate super chakras 8–12 for evolutionary breakthrough." />
      <meta name="keywords" content="UMANA Singapore, chakra 8-12 activation, super chakra healing, energy healing Singapore, soul empowerment" />
      <link rel="canonical" href={`${siteUrl}/services/umana`} />
      <meta property="og:title" content="UMANA 能量疗愈 新加坡 | UMANA Healing Singapore" />
      <meta property="og:description" content="UMANA激活第8-12脉轮，带来意识转变与灵魂赋能。新加坡专业疗愈师Susie，可远程或面对面疗愈。" />
      <meta property="og:url" content={`${siteUrl}/services/umana`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={studioImage} />
      <SEOJsonLd id="umana-jsonld" data={umanaJsonLd} />

      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=serene%20healing%20sanctuary%20with%20emerald%20green%20energy%20flowing%20through%20body%20mind%20and%20spirit%2C%20holistic%20wellness%20visualization%2C%20peaceful%20integration%20of%20physical%20emotional%20and%20spiritual%20elements%2C%20soft%20natural%20light%2C%20harmonious%20balance%2C%20tranquil%20healing%20space%20with%20sacred%20geometry&width=1920&height=1080&seq=umana-hero-bg&orientation=landscape"
            alt="UMANA Healing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/60 via-emerald-900/40 to-emerald-900/60"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-emerald-500/20 backdrop-blur-sm rounded-full border border-emerald-400/30 mb-4 md:mb-5">
            <span className="text-emerald-300 text-xs md:text-sm font-medium tracking-widest">SPIRITUAL HEALING</span>
          </div>
          <h1 className="text-4xl md:text-7xl font-bold text-white mb-4 md:mb-5 tracking-tight">UMANA</h1>
          <p className="text-base md:text-xl text-stone-200 leading-relaxed">
            {t('umanaDetail.hero.description')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col-reverse md:grid md:grid-cols-2 md:gap-16 md:items-center gap-8">
            <div className="relative w-full h-64 md:h-[520px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=peaceful%20person%20in%20meditation%20with%20emerald%20green%20healing%20light%20surrounding%20entire%20body%2C%20holistic%20wellness%20scene%2C%20integration%20of%20mind%20body%20spirit%2C%20gentle%20energy%20flow%2C%20natural%20healing%20environment%2C%20serene%20and%20balanced%20atmosphere%2C%20soft%20glowing%20aura&width=800&height=1000&seq=umana-intro-img&orientation=portrait"
                alt="UMANA Integration"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-emerald-600 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">Introduction</p>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4 md:mb-6 leading-snug">
                {t('umanaDetail.intro.title')}
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                {t('umanaDetail.intro.content')}
              </p>
              <div className="mt-6 md:mt-10 grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { icon: 'ri-heart-pulse-line', label: t('umanaDetail.benefits.benefit1.title') },
                  { icon: 'ri-refresh-line', label: t('umanaDetail.benefits.benefit2.title') },
                  { icon: 'ri-focus-3-line', label: t('umanaDetail.benefits.benefit3.title') },
                  { icon: 'ri-body-scan-line', label: t('umanaDetail.benefits.benefit4.title') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:gap-3 bg-emerald-50 rounded-xl px-3 md:px-4 py-2.5 md:py-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-emerald-600 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-lg`}></i>
                    </div>
                    <span className="text-stone-700 text-xs md:text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Super Chakras — Extra-Physical */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-gradient-to-b from-emerald-50/40 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-emerald-600 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">8–12 Chakras</p>
            <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-3 md:mb-4">
              {t('umanaDetail.superChakras.title')}
            </h2>
          </div>
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10 items-start">
            {/* Image */}
            <div className="flex justify-center">
              <div className="relative rounded-2xl overflow-hidden border border-emerald-100 bg-stone-50/60 p-4 md:p-6 max-w-xs md:max-w-sm w-full">
                <img
                  src="https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/6e940fab99da49b215ef4ca3c031d056.png"
                  alt={t('umanaDetail.superChakras.title')}
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            {/* Text Content */}
            <div className="flex flex-col gap-7">
              {/* Quote */}
              <blockquote className="border-l-4 border-emerald-400 pl-5 py-1">
                <p className="text-stone-600 text-sm md:text-base leading-relaxed italic">
                  &ldquo;{t('umanaDetail.superChakras.quote')}&rdquo;
                </p>
                <footer className="mt-2 text-emerald-600 text-xs md:text-sm font-semibold">
                  {t('umanaDetail.superChakras.quoteAuthor')}
                </footer>
              </blockquote>

              {/* Intro */}
              <p className="text-stone-700 text-sm md:text-base font-medium leading-relaxed">
                {t('umanaDetail.superChakras.intro')}
              </p>

              {/* Chakra List */}
              <ul className="space-y-4">
                {(t('umanaDetail.superChakras.chakras', { returnObjects: true }) as { number: string; desc: string }[]).map((item, i) => (
                  <li key={i} className="flex gap-4 p-4 bg-white rounded-xl border border-emerald-100">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                        {i + 8}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-emerald-700 text-sm mb-0.5">{item.number}</p>
                      <p className="text-stone-600 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Summary */}
              <p className="text-stone-700 text-sm md:text-base leading-relaxed bg-emerald-50/60 border-l-4 border-emerald-300 pl-5 py-3 rounded-r-xl">
                {t('umanaDetail.superChakras.summary')}
              </p>

              {/* Susie Note */}
              <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 mt-1">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-award-fill text-emerald-500 text-base"></i>
                </div>
                <p className="text-stone-700 text-xs md:text-sm leading-relaxed">
                  {t('umanaDetail.superChakras.susieNote')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Suitable For & Method */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-emerald-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10">
            {/* 适合人群 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('umanaDetail.suitable.title')}</h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('umanaDetail.suitable.item1'),
                  t('umanaDetail.suitable.item2'),
                  t('umanaDetail.suitable.item3'),
                  t('umanaDetail.suitable.item4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-600 text-sm md:text-base">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-base"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* 疗愈方式 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <i className="ri-magic-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('umanaDetail.method.title')}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-scan-line', title: t('umanaDetail.method.step1.title'), desc: t('umanaDetail.method.step1.desc') },
                  { icon: 'ri-refresh-line', title: t('umanaDetail.method.step2.title'), desc: t('umanaDetail.method.step2.desc') },
                  { icon: 'ri-shield-star-line', title: t('umanaDetail.method.step3.title'), desc: t('umanaDetail.method.step3.desc') },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-emerald-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm md:text-base mb-1">{item.title}</p>
                      <p className="text-stone-500 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('umanaDetail.pricing.title')}</h3>
          </div>
          <p className="text-stone-400 text-sm mb-8">{t('umanaDetail.pricing.note')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {(t('umanaDetail.pricing.items', { returnObjects: true }) as { duration: string; price: string; label: string }[]).map((item, i) => (
              <div key={i} className="flex-1 max-w-xs mx-auto sm:mx-0 bg-emerald-50 border border-emerald-100 rounded-2xl px-8 py-7 flex flex-col items-center gap-2 shadow-sm">
                <span className="text-xs font-semibold tracking-widest text-emerald-500 uppercase mb-1">{item.label}</span>
                <div className="flex items-center gap-2 text-stone-700">
                  <div className="w-5 h-5 flex items-center justify-center text-emerald-400">
                    <i className="ri-time-line text-base"></i>
                  </div>
                  <span className="text-base font-medium">{item.duration}</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-emerald-600 mt-1">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <WhatsAppBooking accentColor="emerald" serviceName="UMANA" />

      {/* Back */}
      <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-white">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 px-6 md:px-7 py-3 border border-stone-300 text-stone-700 rounded-full hover:bg-stone-100 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t('umanaDetail.cta.back')}</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
